import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Input,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  Sheet,
  Stack,
  Tooltip,
  Typography,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const STORAGE_KEY = 'agentListCollapsed';

const normalizeKeyPart = (value, fallback = 'unknown') => {
  const normalized = String(value ?? '').trim();
  return normalized || fallback;
};

const getAgentRenderKey = (agent, index, groupName) => {
  const normalizedId = String(agent?.id ?? '').trim();
  if (normalizedId) return `id:${normalizedId}`;

  const createdAtPart = normalizeKeyPart(agent?.created_at || agent?.timestamp, 'no-date');
  const messagePart = normalizeKeyPart(agent?.message?.slice(0, 60), 'no-message');

  return `fallback:${groupName}:${createdAtPart}:${messagePart}:${index}`;
};

// Group agents by date
const groupAgentsByDate = (agents) => {
  const now = dayjs();
  const today = now.startOf('day');
  const yesterday = today.subtract(1, 'day');
  const lastWeek = today.subtract(7, 'day');
  const lastMonth = today.subtract(30, 'day');

  const groups = {
    today: [],
    yesterday: [],
    lastWeek: [],
    lastMonth: [],
    older: [],
  };

  agents.forEach((agent) => {
    const createdAt = dayjs(agent.created_at || agent.timestamp);
    
    if (createdAt.isAfter(today)) {
      groups.today.push(agent);
    } else if (createdAt.isAfter(yesterday)) {
      groups.yesterday.push(agent);
    } else if (createdAt.isAfter(lastWeek)) {
      groups.lastWeek.push(agent);
    } else if (createdAt.isAfter(lastMonth)) {
      groups.lastMonth.push(agent);
    } else {
      groups.older.push(agent);
    }
  });

  return groups;
};

const GroupLabel = ({ label }) => (
  <Typography
    level="body-xs"
    sx={{
      px: 2,
      py: 1,
      color: 'text.tertiary',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    }}
  >
    {label}
  </Typography>
);

const AgentListItem = ({ agent, isSelected, isCreating, onSelect, onDelete }) => {
  const displayTitle = agent.message?.slice(0, 50) || `Conversation ${agent.id}`;
  const truncatedTitle = displayTitle.length > 40
    ? displayTitle.slice(0, 40) + '…'
    : displayTitle;

  return (
    <ListItem sx={{ px: 1, py: 0.25 }}>
      <ListItemButton
        selected={isSelected && !isCreating}
        onClick={() => onSelect(agent.id)}
        sx={{
          borderRadius: 'md',
          py: 1.25,
          px: 1.5,
          gap: 1.5,
          transition: 'all 0.15s ease',
          '& .delete-btn': { opacity: 0 },
          '&:hover .delete-btn': { opacity: 1 },
          '&.Mui-selected': {
            bgcolor: 'primary.softBg',
            '&:hover': {
              bgcolor: 'primary.softHoverBg',
            },
          },
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 'md',
            bgcolor: isSelected && !isCreating ? 'primary.100' : 'neutral.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <ChatBubbleOutlineIcon
            sx={{
              fontSize: 16,
              color: isSelected && !isCreating ? 'primary.600' : 'neutral.500',
            }}
          />
        </Box>
        <ListItemContent sx={{ minWidth: 0 }}>
          <Typography
            level="body-sm"
            sx={{
              fontWeight: isSelected && !isCreating ? 600 : 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {truncatedTitle}
          </Typography>
          <Typography
            level="body-xs"
            color="neutral"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {agent.created_at
              ? dayjs(agent.created_at).fromNow()
              : `ID: ${agent.id}`}
          </Typography>
        </ListItemContent>
        <IconButton
          className="delete-btn"
          size="sm"
          variant="plain"
          color="danger"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(agent.id);
          }}
          sx={{ '--IconButton-size': '24px', flexShrink: 0 }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </ListItemButton>
    </ListItem>
  );
};

const MotionSheet = motion.create(Sheet);

const AgentList = ({
  agents,
  agentsLoading,
  selectedAgentId,
  creatingNewAgent = false,
  onSelectAgent,
  onCreateNew,
  onDeleteAgent = () => {},
  onDeleteAll = () => {},
}) => {
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'true';
  });
  const [searchQuery, setSearchQuery] = useState('');

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });
  };

  // Filter agents by search query
  const filteredAgents = useMemo(() => {
    if (!searchQuery.trim()) return agents;
    const query = searchQuery.toLowerCase();
    return agents.filter(
      (agent) =>
        agent.message?.toLowerCase().includes(query) ||
        String(agent.id).includes(query)
    );
  }, [agents, searchQuery]);

  // Group filtered agents by date
  const groupedAgents = useMemo(() => groupAgentsByDate(filteredAgents), [filteredAgents]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;

    const invalidAgents = filteredAgents.filter(
      (agent) => !String(agent?.id ?? '').trim()
    );
    if (invalidAgents.length === 0) return;

    console.warn(
      `[AgentList] ${invalidAgents.length} conversation(s) missing a valid id; using fallback render keys.`,
      invalidAgents.slice(0, 3)
    );
  }, [filteredAgents]);

  return (
    <MotionSheet
      variant="outlined"
      initial={false}
      animate={{
        width: collapsed ? 56 : 300,
      }}
      transition={{
        duration: 0.25,
        ease: [0.4, 0, 0.2, 1],
      }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        borderRadius: 0,
        borderTop: 'none',
        borderBottom: 'none',
        borderLeft: 'none',
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      {/* Collapsed view */}
      <AnimatePresence mode="wait">
        {collapsed ? (
          <motion.div
            key="collapsed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              paddingTop: 12,
              paddingBottom: 12,
              gap: 8,
              height: '100%',
              minHeight: 0,
            }}
          >
            <Tooltip title="Expand" placement="right">
              <IconButton size="sm" variant="plain" onClick={toggleCollapsed}>
                <ChevronRightIcon />
              </IconButton>
            </Tooltip>
            <Divider sx={{ width: '70%', my: 0.5 }} />
            <Tooltip title="New Conversation" placement="right">
              <IconButton
                size="sm"
                variant="solid"
                color="primary"
                onClick={onCreateNew}
                sx={{ borderRadius: 'md' }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </motion.div>
        ) : (
          <motion.div
            key="expanded"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, delay: 0.1 }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Typography level="title-md" sx={{ fontWeight: 600 }}>
          Conversations
        </Typography>
        <Stack direction="row" gap={0.5}>
          <Tooltip title="Delete all conversations">
            <IconButton
              size="sm"
              variant="plain"
              color="neutral"
              onClick={onDeleteAll}
              sx={{ borderRadius: 'md' }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="New Conversation">
            <IconButton
              size="sm"
              variant="solid"
              color="primary"
              onClick={onCreateNew}
              sx={{ borderRadius: 'md' }}
            >
              <AddIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Collapse">
            <IconButton size="sm" variant="plain" onClick={toggleCollapsed}>
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Search */}
      <Box sx={{ px: 1.5, py: 1 }}>
        <Input
          size="sm"
          placeholder="Search conversations…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          startDecorator={<SearchIcon sx={{ fontSize: 18, color: 'neutral.500' }} />}
          sx={{
            '--Input-focusedThickness': '1px',
            borderRadius: 'md',
          }}
        />
      </Box>

      {/* Agent List */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {agentsLoading && agents.length === 0 ? (
          <Stack alignItems="center" sx={{ p: 3 }}>
            <CircularProgress size="sm" />
          </Stack>
        ) : filteredAgents.length === 0 ? (
          <Stack alignItems="center" sx={{ p: 3 }}>
            <Typography level="body-sm" color="neutral" sx={{ textAlign: 'center' }}>
              {searchQuery ? 'No matching conversations' : 'No conversations yet'}
            </Typography>
            {!searchQuery && (
              <Typography level="body-xs" color="neutral" sx={{ mt: 0.5, textAlign: 'center' }}>
                Click + to start one
              </Typography>
            )}
          </Stack>
        ) : (
          <List size="sm" sx={{ p: 0 }}>
            <AnimatePresence mode="popLayout">
              {/* New Conversation Indicator */}
              {creatingNewAgent && (
                <motion.div
                  key="new-conversation-indicator"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ListItem sx={{ px: 1, py: 0.25 }}>
                    <ListItemButton
                      selected
                      sx={{
                        borderRadius: 'md',
                        py: 1.25,
                        px: 1.5,
                        gap: 1.5,
                        bgcolor: 'primary.softBg',
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 'md',
                          bgcolor: 'primary.100',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <AddIcon sx={{ fontSize: 16, color: 'primary.600' }} />
                      </Box>
                      <ListItemContent>
                        <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                          New Conversation
                        </Typography>
                        <Typography level="body-xs" color="primary">
                          Draft
                        </Typography>
                      </ListItemContent>
                    </ListItemButton>
                  </ListItem>
                </motion.div>
              )}

              {/* Today */}
              {groupedAgents.today.length > 0 && (
                <motion.div key="group-today" layout>
                  <GroupLabel label="Today" />
                  {groupedAgents.today.map((agent, index) => (
                    <AgentListItem
                      key={getAgentRenderKey(agent, index, 'today')}
                      agent={agent}
                      isSelected={agent.id === selectedAgentId}
                      isCreating={creatingNewAgent}
                      onSelect={onSelectAgent}
                      onDelete={onDeleteAgent}
                    />
                  ))}
                </motion.div>
              )}

              {/* Yesterday */}
              {groupedAgents.yesterday.length > 0 && (
                <motion.div key="group-yesterday" layout>
                  <GroupLabel label="Yesterday" />
                  {groupedAgents.yesterday.map((agent, index) => (
                    <AgentListItem
                      key={getAgentRenderKey(agent, index, 'yesterday')}
                      agent={agent}
                      isSelected={agent.id === selectedAgentId}
                      isCreating={creatingNewAgent}
                      onSelect={onSelectAgent}
                      onDelete={onDeleteAgent}
                    />
                  ))}
                </motion.div>
              )}

              {/* Last 7 Days */}
              {groupedAgents.lastWeek.length > 0 && (
                <motion.div key="group-last-week" layout>
                  <GroupLabel label="Previous 7 Days" />
                  {groupedAgents.lastWeek.map((agent, index) => (
                    <AgentListItem
                      key={getAgentRenderKey(agent, index, 'last-week')}
                      agent={agent}
                      isSelected={agent.id === selectedAgentId}
                      isCreating={creatingNewAgent}
                      onSelect={onSelectAgent}
                      onDelete={onDeleteAgent}
                    />
                  ))}
                </motion.div>
              )}

              {/* Last 30 Days */}
              {groupedAgents.lastMonth.length > 0 && (
                <motion.div key="group-last-month" layout>
                  <GroupLabel label="Previous 30 Days" />
                  {groupedAgents.lastMonth.map((agent, index) => (
                    <AgentListItem
                      key={getAgentRenderKey(agent, index, 'last-month')}
                      agent={agent}
                      isSelected={agent.id === selectedAgentId}
                      isCreating={creatingNewAgent}
                      onSelect={onSelectAgent}
                      onDelete={onDeleteAgent}
                    />
                  ))}
                </motion.div>
              )}

              {/* Older */}
              {groupedAgents.older.length > 0 && (
                <motion.div key="group-older" layout>
                  <GroupLabel label="Older" />
                  {groupedAgents.older.map((agent, index) => (
                    <AgentListItem
                      key={getAgentRenderKey(agent, index, 'older')}
                      agent={agent}
                      isSelected={agent.id === selectedAgentId}
                      isCreating={creatingNewAgent}
                      onSelect={onSelectAgent}
                      onDelete={onDeleteAgent}
                    />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </List>
        )}
      </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </MotionSheet>
  );
};

export default AgentList;
